import React, { useEffect, useState } from 'react';
import {
    Modal,
    Box,
    TextField,
    Select,
    MenuItem,
    Typography,
    IconButton,
    InputAdornment,
    RadioGroup,
    FormControlLabel,
    Radio,
    useMediaQuery,
    Slider,
    Checkbox,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Spinner } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import CustomButton from '../Button/CustomButton'; // Importing custom button component
import { CustomDatePicker } from '../DatePicker/CustomDatePicker'; // Importing custom date picker component
import ClearIcon from '@mui/icons-material/Clear'; // Importing Clear Icon for "X" button
import { FONT_ENUM } from '../../types/global/fonts.types';
import { FormOption, IRegistrationFormField, TSearchAddCategory } from '../../types/global/forms.types';
import { isEmpty, isObject, isString } from 'lodash';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import dayjs from 'dayjs';
import { CurrencyTextInput } from '../CurrencyTextInput/CurrencyTextInput';
import { CustomizedMultipleInput } from '../CustomizedMultipleInput/CustomizedMultipleInput';
import UploadComponent from '../Upload/UploadComponent';
import PictureUploader from '../PictureUploader/PictureUploader';
import SearchSelectComponent from '../SearchAdd/SearchSelectComponent';
import { ChipGroupWithMultiSelection } from '../Chip/ChipGroupWithMultiSelection';
import { extensionToMimeType } from '../Upload/data';
import _ from 'lodash';
import FormCreator from '../FormCreator/FormCreator';
import { useGoogleMapsLoader } from '../../hook/useMapLoader';
import { isEmptyValue } from '../../utils/lodash';
import CustomizedAutocomplete from '../CustomizedMultipleInput/CustomizedAutocomplete';
import {
    EDITORIAL,
    editorialCharCountSx,
    editorialCloseIconButtonSx,
    editorialDropdownBentoSx,
    editorialFormHeaderTypographySx,
    editorialInputShellSx,
    editorialMenuPaperSx,
    editorialModalContainerSx,
    editorialModalTitleSx,
    editorialOutlinedInputSx,
    editorialRangeSectionSx,
    editorialSelectSx,
    editorialSliderSx,
    editorialStickyFooterSx,
    editorialSubmitButtonSx,
    editorialSubmitTextStyle,
} from './editorialFormStyles';

interface FormGeneratorProps {
    open: boolean;
    onClose: () => void;
    formFields: IRegistrationFormField[];
    header?: string; // Optional header for the form
    onSubmit?: (payload: any) => void;
    onSubmitSuccessful?: () => void;
    onChange?: (storeKey: string, subKey?: string, value?: any) => void;
    onAddClicked?: (category?: TSearchAddCategory, formId?: string, currentFormState?: any, index?: number) => void;
    initialValue?: any;
    initialEditValue?: any;
    isEdit?: boolean;
    manualClose?: boolean;
    loading?: boolean;
    devData?: any;
    initialFormData?: Record<string, any>;
}

const FormGenerator: React.FC<FormGeneratorProps> = ({
    open,
    onClose,
    formFields,
    header,
    onSubmit,
    onSubmitSuccessful,
    onChange,
    onAddClicked,
    initialValue,
    isEdit,
    initialEditValue,
    manualClose,
    loading,
    initialFormData,
    devData,
}) => {
    const [formData, setFormData] = useState<{ [key: string]: any }>(!isEdit ? initialValue : initialEditValue);
    const muiTheme = useTheme();
    // Detect if the screen size is mobile
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    useEffect(() => {
        if (isEdit && initialEditValue) setFormData(initialEditValue);
    }, [initialEditValue, isEdit]);

    const { isLoaded } = useGoogleMapsLoader();

    const handleChange = (storeKey: string, value: string | number | Date | null | Record<any, any> | boolean) => {
        setFormData(prevData => ({
            ...prevData,
            [storeKey]: value,
        }));
    };

    const handleLocationSelect = (storeKey: string, address: string, isMultiple?: boolean) => {
        let description: any;
        let city: string;
        let country: string;
        let payload: any;

        geocodeByAddress(address)
            .then(results => {
                description = results[0].formatted_address;
                const addressComponents = results[0].address_components;
                addressComponents.forEach(component => {
                    const types = component.types;
                    if (types.includes('locality')) {
                        city = component.long_name;
                    }
                    if (types.includes('country')) {
                        country = component.long_name;
                    }
                });

                return getLatLng(results[0]);
            })
            .then(latLng => {
                setFormData(prevData => {
                    // Ensure prevData[storeKey] is treated as an array safely
                    const existingData = Array.isArray(prevData[storeKey]) ? prevData[storeKey] : [];

                    const newEntry = {
                        address: address,
                        type: 'Point',
                        coordinates: [latLng.lng, latLng.lat],
                        description: description,
                        short_name: `${city}, ${country}`,
                    };

                    // If isMultiple, add to the array; otherwise, replace with the new entry
                    payload = isMultiple ? [...existingData, newEntry] : newEntry;

                    return {
                        ...prevData,
                        [storeKey]: payload,
                    };
                });
                onChange && onChange(storeKey, undefined, payload);
            })
            .catch(error => console.error('Error', error));
    };

    const renderHeader = (field: IRegistrationFormField) => {
        return (
            <Typography sx={editorialFormHeaderTypographySx}>
                {field.form_header}{' '}
                {field.required && (
                    <Box component="span" sx={{ color: EDITORIAL.primary, fontSize: 13, fontWeight: 800, ml: 0.25 }}>
                        **
                    </Box>
                )}
            </Typography>
        );
    };

    const handleOnAddClickForSearchSuggestion = (searchCategory?: TSearchAddCategory, formId?: string) => {
        if (searchCategory && formId) {
            onAddClicked && onAddClicked(searchCategory, formId, formData);
        }
    };

    const clearLocationInput = (storeKey: string) => {
        setFormData(prevData => ({
            ...prevData,
            [storeKey]: '',
        }));
    };

    const renderField = (field: IRegistrationFormField) => {
        const fieldStyle = {
            ...editorialOutlinedInputSx,
        };

        if (field && field.dependency) {
            const dependentValue: any = formData ? formData?.[field.dependency.depends_on] : undefined;
            const condition = field.dependency.condition;

            let shouldRender = false;
            if (dependentValue && isObject(dependentValue) && 'value' in dependentValue) {
                shouldRender =
                    condition?.equality === '!=='
                        ? dependentValue.value !== condition.value
                        : dependentValue.value === condition.value;
            } else {
                shouldRender =
                    condition?.equality === '!=='
                        ? dependentValue !== condition.value
                        : dependentValue === condition.value;
            }

            if (isString(dependentValue) || dayjs(dependentValue).isValid()) {
                shouldRender = !isEmpty(dependentValue);
            }

            if (!shouldRender) {
                return null; // Skip rendering the field if dependency is not met
            }
        }

        switch (field.form_type) {
            case 'textInput':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <Box sx={editorialInputShellSx}>
                            <TextField
                                fullWidth
                                margin="dense"
                                name={field.form_name}
                                value={formData?.[field.store_key] || ''}
                                onChange={e =>
                                    handleChange(
                                        field.store_key,
                                        field.textInputProps?.type === 'number'
                                            ? Number(e.target.value)
                                            : e.target.value,
                                    )
                                }
                                sx={fieldStyle}
                                key={field.form_name}
                                InputProps={{
                                    sx: {
                                        fontSize: '14px',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: EDITORIAL.onSurface,
                                    },
                                }}
                                {...field.textInputProps}
                            />
                        </Box>
                        {field.textInputProps!?.maxchars && (
                            <Typography variant="caption" sx={editorialCharCountSx}>
                                {formData[field.store_key]?.length || 0}/{field.textInputProps!?.maxchars}
                            </Typography>
                        )}
                    </div>
                );
            case 'phone':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <PhoneInput
                            onChange={(e: any, data: CountryData) => handleChange(field.store_key, e)}
                            value={(formData?.[field.store_key] as string) || ''}
                            country="us"
                            specialLabel=""
                            inputStyle={{
                                width: '100%',
                                paddingRight: 10,
                                backgroundColor: EDITORIAL.surfaceContainer,
                                border: `1px solid ${EDITORIAL.outlineVariant}`,
                                borderRadius: 12,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: 14,
                                color: EDITORIAL.onSurface,
                                position: 'relative',
                                backgroundSize: '20px',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right',
                                backgroundOrigin: 'content-box',
                                marginTop: '10px',
                            }}
                            dropdownStyle={{
                                backgroundColor: EDITORIAL.surfaceContainer,
                                color: EDITORIAL.onSurface,
                                width: '400px',
                            }}
                            countryCodeEditable={false}
                        />
                    </div>
                );
            case 'dropdown':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <Box sx={editorialDropdownBentoSx}>
                            <Select
                                fullWidth
                                margin="dense"
                                name={field.form_name}
                                value={formData?.[field.store_key] || ''}
                                onChange={e => handleChange(field.store_key, e.target.value as string)}
                                sx={{
                                    ...editorialSelectSx,
                                    mt: 0,
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: editorialMenuPaperSx,
                                    },
                                }}
                                key={field.form_name}
                                displayEmpty>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {(field.form_options as FormOption[]).map(option => (
                                    <MenuItem
                                        value={option.value}
                                        key={option.value}
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: EDITORIAL.onSurface,
                                            textTransform: 'capitalize',
                                        }}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </div>
                );
            case 'pillSelection':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <ChipGroupWithMultiSelection
                            clickable
                            editorial
                            options={field.form_options}
                            onTagClick={tag => handleChange(field.store_key, tag)}
                            initialSelected={(formData?.[field.store_key] as any) || []}
                            multiple={field.pillSelectionProps?.multiple}
                            {...field.pillSelectionProps}
                        />
                    </div>
                );
            case 'location':
                return (
                    isLoaded && (
                        <div key={field.form_name} style={{ marginBottom: '20px' }}>
                            {field.form_header && renderHeader(field)}
                            <PlacesAutocomplete
                                value={(formData?.[field.store_key] as string) || ''}
                                onChange={address => handleChange(field.store_key, address)}
                                onSelect={address => handleLocationSelect(field.store_key, address, field.multiple)}
                                key={field.form_name}>
                                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                                    <div style={{ position: 'relative' }}>
                                        <Box sx={editorialInputShellSx}>
                                            <TextField
                                                fullWidth
                                                margin="dense"
                                                label={field.form_label}
                                                {...getInputProps({
                                                    placeholder: 'Search Location ...',
                                                })}
                                                sx={fieldStyle}
                                                InputProps={{
                                                    endAdornment: formData?.[field.store_key] ? (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => clearLocationInput(field.store_key)}
                                                                edge="end">
                                                                <ClearIcon sx={{ color: EDITORIAL.outline }} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ) : null,
                                                }}
                                            />
                                        </Box>
                                        <div
                                            style={{
                                                position: 'absolute',
                                                zIndex: 10,
                                                backgroundColor: EDITORIAL.surfaceContainer,
                                                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
                                                borderRadius: '12px',
                                                marginTop: '4px',
                                                width: '100%',
                                                border: `1px solid ${EDITORIAL.outlineVariant}`,
                                            }}>
                                            {loading && <div>Loading...</div>}
                                            {suggestions.map(suggestion => {
                                                const style = {
                                                    backgroundColor: suggestion.active
                                                        ? EDITORIAL.surfaceContainerHigh
                                                        : EDITORIAL.surfaceLow,
                                                    cursor: 'pointer',
                                                    padding: '10px',
                                                    color: EDITORIAL.onSurface,
                                                };
                                                return (
                                                    <div
                                                        {...getSuggestionItemProps(suggestion, {
                                                            style,
                                                        })}
                                                        key={suggestion.placeId}>
                                                        {suggestion.description}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </PlacesAutocomplete>
                        </div>
                    )
                );
            case 'calendar':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <CustomDatePicker
                            onChange={(newDate, timezone) => {
                                handleChange(field.store_key, newDate);
                                if (timezone) {
                                    handleChange('timezone', timezone);
                                }
                            }}
                            key={field.form_name}
                            maxDate={
                                field.calendarProps &&
                                field.calendarProps?.maxDate &&
                                dayjs(dayjs().subtract(field.calendarProps?.maxDate, 'year').format('YYYY-MM-DD'))
                            }
                            minDate={
                                field.calendarProps?.minDateKey && formData?.[field.calendarProps?.minDateKey]
                                    ? formData?.[field.calendarProps?.minDateKey]
                                    : field.calendarProps &&
                                      field.calendarProps?.minDate &&
                                      dayjs(dayjs().subtract(field.calendarProps?.minDate, 'year').format('YYYY-MM-DD'))
                            }
                            value={(formData?.[field.store_key] as string) || ''}
                            withTime={field.calendarProps?.withTime}
                            sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { bgcolor: EDITORIAL.surfaceContainer } }}
                            withTimezone={field.calendarProps?.withTime}
                            {...(field.calendarProps || {})}
                        />
                    </div>
                );
            case 'radioSelection':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <RadioGroup
                            row
                            name={field.form_name}
                            value={formData?.[field.store_key] || ''}
                            onChange={e => handleChange(field.store_key, e.target.value)}>
                            {(field.form_options as FormOption[]).map(option => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={
                                        <Radio
                                            sx={{
                                                color: EDITORIAL.outline,
                                                '&.Mui-checked': { color: EDITORIAL.primary },
                                            }}
                                        />
                                    }
                                    label={option.label}
                                    sx={{
                                        color: EDITORIAL.onSurfaceVariant,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontWeight: 600,
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </div>
                );
            case 'range':
                return (
                    <Box sx={editorialRangeSectionSx} key={field.form_name}>
                        {field.form_header && renderHeader(field)}
                        <Slider
                            value={(formData?.[field.store_key] as unknown as number[]) || 0}
                            onChange={(event, newValue) => handleChange(field.store_key, newValue as any)}
                            valueLabelDisplay="auto"
                            sx={editorialSliderSx}
                            {...field.sliderProps}
                        />
                    </Box>
                );
            case 'slider':
                return (
                    <Box sx={editorialRangeSectionSx} key={field.form_name}>
                        {field.form_header && renderHeader(field)}
                        <Slider
                            value={(formData?.[field.store_key] as unknown as number) || 0}
                            onChange={(event, newValue) => handleChange(field.store_key, newValue as any)}
                            valueLabelDisplay="auto"
                            sx={editorialSliderSx}
                            {...field.sliderProps}
                        />
                    </Box>
                );
            case 'currency':
                return (
                    <Box sx={{ py: 1, mb: 2 }} key={field.form_name}>
                        {field.form_header && renderHeader(field)}
                        <CurrencyTextInput
                            value={(formData?.[field.store_key] as unknown as number) || 0}
                            minAmount={field.textInputProps?.minAmount}
                            details={field.additional_store_keys?.map(key => formData?.[key])}
                            supportedCurrency={field.currencyTextInputProps?.supportedCurrency}
                            message={field.textInputProps?.lowerMessage}
                            onChange={({ currency, value }) => {
                                if (field.additional_store_keys) {
                                    handleChange(field.additional_store_keys[0], currency as any);
                                    handleChange(field.additional_store_keys[1], value as any);
                                }
                            }}
                            sx={{
                                color: EDITORIAL.onSurface,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: EDITORIAL.outlineVariant,
                                },
                            }}
                        />
                    </Box>
                );
            case 'suggestionTextInput':
                const singleSelection =
                    field.suggestionTextInputProps &&
                    field.suggestionTextInputProps?.maximumSelectable &&
                    field.suggestionTextInputProps?.maximumSelectable < 2;
                let list = field.suggestion!;

                /**
                 * Breakdown of this
                 * Check if we have dependencyField in the props
                 * if it exists and enable dyamic list is true, then
                 * use the dependecy value to fill the options; defaults
                 * to the top suggestion when not present.
                 */

                if (
                    field.suggestionTextInputProps &&
                    field.suggestionTextInputProps?.enableDynamicList &&
                    field.suggestionTextInputProps.dynamicList &&
                    field.suggestionTextInputProps.dependencyField &&
                    initialFormData
                ) {
                    const dynamicList = field.suggestionTextInputProps.dynamicList || [];
                    const dependecyField = field.suggestionTextInputProps.dependencyField;
                    const depValue = initialFormData?.[dependecyField];

                    const value =
                        typeof depValue === 'string'
                            ? depValue
                            : depValue && typeof depValue === 'object' && 'value' in depValue
                            ? depValue.value
                            : null;

                    if (value && Array.isArray(dynamicList[value]) && !isEmpty(dynamicList[value])) {
                        list = dynamicList[value];
                    }
                }

                return (
                    <div key={field.form_name} style={{ marginBottom: '20px', position: 'relative' }}>
                        {field.form_header && renderHeader(field)}
                        <CustomizedAutocomplete
                            onChange={text => {
                                const newText = singleSelection ? text[0] : text;
                                handleChange(field.store_key, newText);
                            }}
                            options={list}
                            maxSelectable={field?.suggestionTextInputProps?.maximumSelectable ?? 1}
                            initialValue={
                                singleSelection && formData
                                    ? formData?.[field.store_key]
                                        ? [formData?.[field.store_key] as any]
                                        : []
                                    : (formData?.[field.store_key] as any)
                            }
                        />
                    </div>
                );
            case 'search_add':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <SearchSelectComponent
                            initialValue={formData?.[field.store_key] as any}
                            onChange={value => {
                                handleChange(field.store_key, value);
                            }}
                            category={field.search_add_category}
                            onAddClick={() => {
                                handleOnAddClickForSearchSuggestion(
                                    field.search_add_category,
                                    field.search_add_category_form,
                                );
                            }}
                        />
                    </div>
                );
            case 'picture':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <PictureUploader
                            onChange={(images, rawFiles, alternateKey) => {
                                handleChange(field.store_key, images);
                                handleChange('rawFiles', rawFiles as File[]);
                                if (alternateKey) {
                                    handleChange(alternateKey, rawFiles as File[]);
                                }
                            }}
                            onRemove={() => {
                                handleChange(field.store_key, null);
                            }}
                            multiple={field.multiple}
                            initialImageSrc={formData?.[field.store_key]}
                            maxCount={field.max_count}
                            supportedTypes={field.supported_media_types}
                            {...field.pictureProps}
                        />
                    </div>
                );
            case 'upload':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <UploadComponent
                            onChange={value => handleChange(field.store_key, value)}
                            initialFile={formData?.[field.store_key] as any}
                            uploadFormats={extensionToMimeType(field.supported_upload_formats)}
                        />
                    </div>
                );
            case 'video':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        video
                    </div>
                );
            case 'autoComplete':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <CustomizedMultipleInput
                            autoCompleteOptions={[]}
                            placeholder={field.autoCompleteProps?.placeholder}
                            maximumSelectable={field.autoCompleteProps?.maximumSelectable}
                            initialValue={formData?.[field.store_key] as any}
                            onChange={arg => {
                                handleChange(field.store_key, arg);
                            }}
                            entyLimit={2}
                            style={{
                                borderRadius: '12px',
                                backgroundColor: EDITORIAL.surfaceLow,
                                border: `1px solid ${EDITORIAL.outlineVariant}`,
                            }}
                            loading={false} // API Call loading
                            {...field.autoCompleteProps}
                        />
                    </div>
                );
            case 'form_creator':
                return (
                    <div key={field.form_name} style={{ marginBottom: '20px' }}>
                        {field.form_header && renderHeader(field)}
                        <FormCreator
                            initialFields={[]}
                            onChange={updatedFields => {
                                handleChange(field.store_key, updatedFields);
                                onChange && onChange(field.store_key, undefined, updatedFields);
                            }}
                        />
                    </div>
                );
            case 'checkbox':
                return (
                    <div key={field.form_name} style={{ marginTop: '20px', marginBottom: '20px' }}>
                        <FormControlLabel
                            title={field.form_label}
                            control={
                                <Checkbox
                                    checked={(formData[field.store_key] as unknown as boolean) || false}
                                    onChange={() => {
                                        handleChange(field.store_key, !formData[field.store_key]);
                                    }}
                                    sx={{
                                        color: EDITORIAL.outline,
                                        '&.Mui-checked': {
                                            color: EDITORIAL.primary,
                                        },
                                        '&.MuiCheckbox-indeterminate': {
                                            color: EDITORIAL.primary,
                                        },
                                    }}
                                />
                            }
                            label={field.form_label}
                            sx={{
                                color: EDITORIAL.onSurfaceVariant,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontWeight: 600,
                            }}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    const handleSubmit = () => {
        onSubmit && onSubmit(formData);
        if (!manualClose) onClose();
        setFormData({});
    };

    const hasChanged = () => !_.isEqual(initialValue, formData);

    const isFormComplete = () => {
        const fields =
            formFields &&
            formFields.filter(field => {
                // Check if field is rendered based on dependency or other logic
                if (field.dependency) {
                    const dependentValue = formData[field.dependency.depends_on];
                    const condition = field.dependency.condition;
                    let shouldRender = false;

                    if (isObject(dependentValue) && 'value' in dependentValue) {
                        shouldRender =
                            condition?.equality === '!=='
                                ? dependentValue.value !== condition.value
                                : dependentValue.value === condition.value;
                    } else {
                        shouldRender =
                            condition?.equality === '!=='
                                ? dependentValue !== condition.value
                                : dependentValue === condition.value;
                    }

                    if (isString(dependentValue)) {
                        shouldRender = !isEmpty(dependentValue);
                    }

                    return shouldRender; // Include field only if it should render
                }
                return true; // Include all non-dependent fields
            });
        const isComplete = fields.every(field => {
            if (field.required && formData) {
                return !isEmptyValue(formData[field.store_key]);
            }
            return true;
        }); // Check if any field is filled
        return isComplete;
    };

    return (
        <Modal
            open={open}
            style={{
                borderRadius: 20,
            }}
            onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: isMobile ? 0 : '50%',
                    left: isMobile ? 0 : '50%',
                    transform: isMobile ? 'none' : 'translate(-50%, -50%)',
                    width: isMobile ? '100%' : 500,
                    height: isMobile ? '100vh' : 'auto',
                    overflowY: isMobile ? 'scroll' : 'auto',
                    maxHeight: isMobile ? '100vh' : '80vh',
                    overflow: 'scroll',
                    ...editorialModalContainerSx(isMobile),
                }}>
                <AnimatePresence>
                    {loading && (
                        <motion.div
                            initial={{ y: -50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -50, opacity: 0 }}
                            style={{
                                position: 'fixed',
                                top: 10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                            }}>
                            <Spinner h={30} w={30} color={EDITORIAL.primary} />
                        </motion.div>
                    )}
                </AnimatePresence>
                <Typography sx={editorialModalTitleSx}>{header}</Typography>
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 12, top: 12, ...editorialCloseIconButtonSx }}
                    edge="end">
                    <ClearIcon />
                </IconButton>
                <form>
                    {/* Rendering form fields */}
                    {formFields.map(field => renderField(field))}

                    <Box sx={{ ...editorialStickyFooterSx, mt: 3, mb: { xs: 2, sm: 0 } }}>
                        <CustomButton
                            label="Submit"
                            onClick={handleSubmit}
                            customStyle={{ width: '100%', minHeight: 52 }}
                            textStyle={editorialSubmitTextStyle}
                            sx={editorialSubmitButtonSx}
                            disabled={!isFormComplete() || (isEdit && !hasChanged())}
                            loading={loading}
                        />
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export { FormGenerator };
